import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import CharCounter from "./CharCounter";
import PreviaModeloPublicacaoPedido from "./PreviaModeloPublicacaoPedido";
import api from "../services/api";
import empreendimentoService from "../services/empreendimento";
import publicacaoService from "../services/publicacao";
import {
  CHAR_LIMITS,
  categoriasRequerimentoMudancaTitularidade,
  getAtividadeEmpreendimentoLabel,
  modeloPublicacaoSistemaEnum,
  modeloPublicacaoSistemaLabels,
} from "../utils/constants";
import { formatCpfCnpj, validateCpfCnpj } from "../utils/formatters";
import { getApiErrorMessage } from "../utils/getApiErrorMessage";

const emptyForm = {
  nomeOuRazaoSocial: "",
  cnpj: "",
  tipoLicencaId: "",
  textoTipoLicenca: "",
  atividade: "",
  endereco: "",
  numeroProcessoEmTramite: "",
  modelo: modeloPublicacaoSistemaEnum.REQUERIMENTO_LICENCA_AUTORIZACAO,
};

const MENSAGEM_DOCUMENTO_NAO_ENCONTRADO =
  "Não há empreendimento cadastrado com o CPF/CNPJ informado.";

const MENSAGEM_SELECIONE_EMPREENDIMENTO =
  "Selecione o empreendimento para o qual a publicação será criada.";

const HELPER_TEXTO_TIPO_LICENCA =
  "Incluir o tipo de licença ambiental nos termos do Art. 4º da Resolução Coema 02/2019.";

const HELPER_TEXTO_TIPO_LICENCA_ART_4_E_6 =
  "Incluir o tipo de licença/autorização ambiental nos termos do Art. 4º e 6º da Resolução Coema 02/2019.";

function helperTextoTipoLicenca(modelo) {
  const codigo = Number(modelo);
  if (
    codigo === modeloPublicacaoSistemaEnum.REQUERIMENTO_LICENCA_AUTORIZACAO_AMBIENTAL ||
    codigo === modeloPublicacaoSistemaEnum.REQUERIMENTO_RENOVACAO_LICENCA_AUTORIZACAO
  ) {
    return HELPER_TEXTO_TIPO_LICENCA_ART_4_E_6;
  }
  return HELPER_TEXTO_TIPO_LICENCA;
}

function ehModeloComPrevia(modelo) {
  const codigo = Number(modelo);
  return (
    codigo === modeloPublicacaoSistemaEnum.REQUERIMENTO_LICENCA_AUTORIZACAO ||
    codigo === modeloPublicacaoSistemaEnum.REQUERIMENTO_LICENCA_AUTORIZACAO_AMBIENTAL ||
    codigo === modeloPublicacaoSistemaEnum.REQUERIMENTO_RENOVACAO_LICENCA_AUTORIZACAO ||
    codigo === modeloPublicacaoSistemaEnum.REQUERIMENTO_REGULARIZACAO_LICENCA ||
    ehModeloNovoTitular(modelo)
  );
}

const HELPER_CATEGORIA_MUDANCA =
  "Incluir a categoria da licença (renovação ou regularização) ou autorização ambiental.";

function ehModeloNovoTitular(modelo) {
  const codigo = Number(modelo);
  return (
    codigo === modeloPublicacaoSistemaEnum.REQUERIMENTO_MUDANCA_TITULARIDADE ||
    codigo ===
      modeloPublicacaoSistemaEnum.REQUERIMENTO_MUDANCA_TITULARIDADE_PROCESSO_EM_TRAMITE
  );
}

function ehModeloProcessoEmTramite(modelo) {
  return (
    Number(modelo) ===
    modeloPublicacaoSistemaEnum.REQUERIMENTO_MUDANCA_TITULARIDADE_PROCESSO_EM_TRAMITE
  );
}

function downloadBlob(blob, fileName) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function formatarEndereco(endereco) {
  if (!endereco) return "";
  const numero =
    endereco.numero != null && endereco.numero !== ""
      ? `, ${endereco.numero}`
      : "";
  const municipio = endereco.municipio || "";
  const estado = (endereco.estado || "").toUpperCase();
  const cidadeUf = [municipio, estado].filter(Boolean).join("/");
  const complemento = endereco.complemento ? ` - ${endereco.complemento}` : "";
  return `${endereco.rua || ""}${numero}${complemento}${cidadeUf ? ` - ${cidadeUf}` : ""}`.trim();
}

function extrairAtividade(empreendimento) {
  const atividadeLabel = getAtividadeEmpreendimentoLabel(
    empreendimento?.atividadeEmpreendimento,
  );
  return (atividadeLabel !== "-" ? atividadeLabel : "").slice(
    0,
    CHAR_LIMITS.atividadePublicacao,
  );
}

function extrairEndereco(empreendimento) {
  return formatarEndereco(
    empreendimento?.enderecoAtividade || empreendimento?.endereco,
  ).slice(0, CHAR_LIMITS.enderecoPublicacao);
}

function obterIdEmpreendimento(empreendimento) {
  return String(empreendimento?.id ?? empreendimento?.Id ?? "");
}

function rotuloBaseCandidato(empreendimento) {
  const nome = empreendimento?.nome || empreendimento?.Nome || "Empreendimento";
  const atividade = getAtividadeEmpreendimentoLabel(
    empreendimento?.atividadeEmpreendimento ??
      empreendimento?.AtividadeEmpreendimento,
  );
  return `${nome} — ${atividade}`;
}

function rotuloCandidato(empreendimento, candidatos) {
  const rotulo = rotuloBaseCandidato(empreendimento);
  const duplicados = candidatos.filter(
    (candidato) => rotuloBaseCandidato(candidato) === rotulo,
  );
  if (duplicados.length <= 1) return rotulo;

  const endereco = extrairEndereco(empreendimento);
  return endereco ? `${rotulo} — ${endereco}` : rotulo;
}

function dadosDoEmpreendimento(empreendimento) {
  const nome = (
    empreendimento?.nome ||
    empreendimento?.Nome ||
    ""
  ).slice(0, CHAR_LIMITS.nomeOuRazaoSocial);

  return {
    nomeOuRazaoSocial: nome,
    atividade: extrairAtividade(empreendimento),
    endereco: extrairEndereco(empreendimento),
  };
}

export default function CriarPublicacaoPedidoModal({
  open,
  onClose,
  onSucesso,
}) {
  const [form, setForm] = useState(emptyForm);
  const [tiposLicenca, setTiposLicenca] = useState([]);
  const [modelos, setModelos] = useState([]);
  const [loadingTipos, setLoadingTipos] = useState(false);
  const [loading, setLoading] = useState(false);
  const [buscandoEmpreendimento, setBuscandoEmpreendimento] = useState(false);
  const [empreendimentoEncontrado, setEmpreendimentoEncontrado] = useState(false);
  const [candidatos, setCandidatos] = useState([]);
  const [empreendimentoIdSelecionado, setEmpreendimentoIdSelecionado] =
    useState("");
  const [error, setError] = useState("");
  const [pedidoCriado, setPedidoCriado] = useState(null);
  const [baixandoComprovante, setBaixandoComprovante] = useState(false);
  const buscaDocumentoRef = useRef(0);
  const conteudoRef = useRef(null);

  useEffect(() => {
    if (!error) return;
    conteudoRef.current?.scrollTo?.({ top: 0, behavior: "smooth" });
  }, [error]);

  useEffect(() => {
    if (!open) return;

    setForm(emptyForm);
    setError("");
    setPedidoCriado(null);
    setLoading(false);
    setBuscandoEmpreendimento(false);
    setEmpreendimentoEncontrado(false);
    setCandidatos([]);
    setEmpreendimentoIdSelecionado("");
    buscaDocumentoRef.current += 1;

    const carregarDadosIniciais = async () => {
      setLoadingTipos(true);
      try {
        const [tiposResponse, modelosLista] = await Promise.all([
          api.get("TipoLicenca/GetByNomeAsync", { params: { nome: "" } }),
          publicacaoService.listarModelosPedido().catch(() => []),
        ]);
        const lista = Array.isArray(tiposResponse.data) ? tiposResponse.data : [];
        setTiposLicenca(
          lista.filter((tipo) => tipo.isActive ?? tipo.IsActive ?? true),
        );
        setModelos(
          Array.isArray(modelosLista) && modelosLista.length > 0
            ? modelosLista
            : [
                {
                  codigo: modeloPublicacaoSistemaEnum.REQUERIMENTO_LICENCA_AUTORIZACAO,
                  nome: modeloPublicacaoSistemaLabels[
                    modeloPublicacaoSistemaEnum.REQUERIMENTO_LICENCA_AUTORIZACAO
                  ],
                },
                {
                  codigo:
                    modeloPublicacaoSistemaEnum.REQUERIMENTO_LICENCA_AUTORIZACAO_AMBIENTAL,
                  nome: modeloPublicacaoSistemaLabels[
                    modeloPublicacaoSistemaEnum.REQUERIMENTO_LICENCA_AUTORIZACAO_AMBIENTAL
                  ],
                },
                {
                  codigo:
                    modeloPublicacaoSistemaEnum.REQUERIMENTO_RENOVACAO_LICENCA_AUTORIZACAO,
                  nome: modeloPublicacaoSistemaLabels[
                    modeloPublicacaoSistemaEnum.REQUERIMENTO_RENOVACAO_LICENCA_AUTORIZACAO
                  ],
                },
                {
                  codigo:
                    modeloPublicacaoSistemaEnum.REQUERIMENTO_REGULARIZACAO_LICENCA,
                  nome: modeloPublicacaoSistemaLabels[
                    modeloPublicacaoSistemaEnum.REQUERIMENTO_REGULARIZACAO_LICENCA
                  ],
                },
                {
                  codigo:
                    modeloPublicacaoSistemaEnum.REQUERIMENTO_MUDANCA_TITULARIDADE,
                  nome: modeloPublicacaoSistemaLabels[
                    modeloPublicacaoSistemaEnum.REQUERIMENTO_MUDANCA_TITULARIDADE
                  ],
                },
                {
                  codigo:
                    modeloPublicacaoSistemaEnum.REQUERIMENTO_MUDANCA_TITULARIDADE_PROCESSO_EM_TRAMITE,
                  nome: modeloPublicacaoSistemaLabels[
                    modeloPublicacaoSistemaEnum
                      .REQUERIMENTO_MUDANCA_TITULARIDADE_PROCESSO_EM_TRAMITE
                  ],
                },
              ],
        );
      } catch (err) {
        setError(
          getApiErrorMessage(err, "Não foi possível carregar os dados iniciais."),
        );
      } finally {
        setLoadingTipos(false);
      }
    };

    carregarDadosIniciais();
  }, [open]);

  const limparDadosEmpreendimento = () => {
    setEmpreendimentoEncontrado(false);
    setCandidatos([]);
    setEmpreendimentoIdSelecionado("");
    setForm((prev) => ({
      ...prev,
      nomeOuRazaoSocial: "",
      atividade: "",
      endereco: "",
    }));
  };

  const buscarEmpreendimentoPorDocumento = async (documentoFormatado) => {
    const digitos = documentoFormatado.replace(/\D/g, "");
    if (!validateCpfCnpj(digitos)) {
      limparDadosEmpreendimento();
      return;
    }

    const requestId = ++buscaDocumentoRef.current;
    setBuscandoEmpreendimento(true);
    setError("");

    try {
      const lista = await empreendimentoService.obterPorCnpj(documentoFormatado);
      if (requestId !== buscaDocumentoRef.current) return;

      const encontrados = Array.isArray(lista) ? lista : [];
      setCandidatos(encontrados);

      if (encontrados.length === 1) {
        const unico = encontrados[0];
        setEmpreendimentoIdSelecionado(obterIdEmpreendimento(unico));
        setForm((prev) => ({
          ...prev,
          ...dadosDoEmpreendimento(unico),
        }));
        setEmpreendimentoEncontrado(true);
        setError("");
        return;
      }

      setEmpreendimentoIdSelecionado("");
      setEmpreendimentoEncontrado(false);
      setForm((prev) => ({
        ...prev,
        nomeOuRazaoSocial: "",
        atividade: "",
        endereco: "",
      }));
      setError("");
    } catch (err) {
      if (requestId !== buscaDocumentoRef.current) return;
      limparDadosEmpreendimento();
      setError(
        err?.notFound
          ? MENSAGEM_DOCUMENTO_NAO_ENCONTRADO
          : getApiErrorMessage(err, MENSAGEM_DOCUMENTO_NAO_ENCONTRADO),
      );
    } finally {
      if (requestId === buscaDocumentoRef.current) {
        setBuscandoEmpreendimento(false);
      }
    }
  };

  const handleClose = () => {
    if (loading || baixandoComprovante || buscandoEmpreendimento) return;
    onClose();
  };

  const handleSelecionarEmpreendimento = (event) => {
    const id = event.target.value;
    setEmpreendimentoIdSelecionado(id);
    const escolhido = candidatos.find(
      (candidato) => obterIdEmpreendimento(candidato) === String(id),
    );
    if (!escolhido) {
      setEmpreendimentoEncontrado(false);
      return;
    }

    setForm((prev) => ({
      ...prev,
      ...dadosDoEmpreendimento(escolhido),
    }));
    setEmpreendimentoEncontrado(true);
    setError("");
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name === "cnpj") {
      const documentoFormatado = formatCpfCnpj(value);
      setForm((prev) => ({ ...prev, cnpj: documentoFormatado }));
      const digitos = documentoFormatado.replace(/\D/g, "");
      if (validateCpfCnpj(digitos)) {
        buscarEmpreendimentoPorDocumento(documentoFormatado);
        return;
      }

      limparDadosEmpreendimento();
      setError("");
      return;
    }

    if (name === "modelo") {
      setForm((prev) => {
        const entrandoMudanca = ehModeloNovoTitular(value);
        const saindoMudanca = ehModeloNovoTitular(prev.modelo) && !entrandoMudanca;
        let textoTipoLicenca = prev.textoTipoLicenca;
        if (
          entrandoMudanca &&
          !categoriasRequerimentoMudancaTitularidade.includes(textoTipoLicenca)
        ) {
          textoTipoLicenca = "";
        }
        if (
          saindoMudanca &&
          categoriasRequerimentoMudancaTitularidade.includes(textoTipoLicenca)
        ) {
          const tipo = tiposLicenca.find(
            (item) => item.id === prev.tipoLicencaId || item.Id === prev.tipoLicencaId,
          );
          textoTipoLicenca = (tipo?.nome || tipo?.Nome || "").slice(
            0,
            CHAR_LIMITS.textoTipoLicenca,
          );
        }
        return {
          ...prev,
          modelo: value,
          textoTipoLicenca,
          numeroProcessoEmTramite: ehModeloProcessoEmTramite(value)
            ? prev.numeroProcessoEmTramite
            : "",
        };
      });
      return;
    }

    if (name === "tipoLicencaId") {
      const tipo = tiposLicenca.find((t) => t.id === value || t.Id === value);
      const nomeTipo = tipo?.nome || tipo?.Nome || "";
      setForm((prev) => ({
        ...prev,
        tipoLicencaId: value,
        textoTipoLicenca: ehModeloNovoTitular(prev.modelo)
          ? prev.textoTipoLicenca
          : prev.textoTipoLicenca.trim()
            ? prev.textoTipoLicenca
            : nomeTipo.slice(0, CHAR_LIMITS.textoTipoLicenca),
      }));
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const validar = () => {
    const digitos = form.cnpj.replace(/\D/g, "");
    if (!validateCpfCnpj(digitos)) {
      return "CPF/CNPJ inválido.";
    }
    if (candidatos.length > 1 && !empreendimentoIdSelecionado) {
      return MENSAGEM_SELECIONE_EMPREENDIMENTO;
    }
    if (!empreendimentoEncontrado) {
      return MENSAGEM_DOCUMENTO_NAO_ENCONTRADO;
    }
    const mudancaTitularidade = ehModeloNovoTitular(form.modelo);
    if (!form.nomeOuRazaoSocial.trim()) {
      return mudancaTitularidade
        ? "Informe o nome do novo titular."
        : "Informe o nome ou razão social.";
    }
    if (form.nomeOuRazaoSocial.length > CHAR_LIMITS.nomeOuRazaoSocial) {
      return `Nome/razão social deve ter no máximo ${CHAR_LIMITS.nomeOuRazaoSocial} caracteres.`;
    }
    if (!form.tipoLicencaId) {
      return "Selecione o tipo de licença.";
    }
    if (mudancaTitularidade) {
      if (!categoriasRequerimentoMudancaTitularidade.includes(form.textoTipoLicenca)) {
        return "Selecione a categoria da licença.";
      }
    } else if (!form.textoTipoLicenca.trim()) {
      return "Informe o texto do tipo de licença/autorização.";
    } else if (form.textoTipoLicenca.length > CHAR_LIMITS.textoTipoLicenca) {
      return `Texto do tipo de licença deve ter no máximo ${CHAR_LIMITS.textoTipoLicenca} caracteres.`;
    }
    if (ehModeloProcessoEmTramite(form.modelo)) {
      const numero = form.numeroProcessoEmTramite.trim();
      if (!numero) {
        return "Informe o número do processo em trâmite na SEMAETS.";
      }
      if (numero.length > CHAR_LIMITS.numeroProcessoEmTramite) {
        return `O número do processo deve ter no máximo ${CHAR_LIMITS.numeroProcessoEmTramite} caracteres.`;
      }
    }
    if (!form.atividade.trim()) {
      return "A atividade do empreendimento é obrigatória.";
    }
    if (!form.endereco.trim()) {
      return "O endereço do empreendimento é obrigatório.";
    }
    if (!form.modelo) {
      return "Selecione o modelo de publicação.";
    }
    return "";
  };

  const handleSubmit = async () => {
    const validationError = validar();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError("");
    try {
      const created = await publicacaoService.criarPedido({
        nomeOuRazaoSocial: form.nomeOuRazaoSocial.trim(),
        cnpj: form.cnpj,
        tipoLicencaId: form.tipoLicencaId,
        textoTipoLicenca: form.textoTipoLicenca.trim(),
        atividade: form.atividade.trim(),
        endereco: form.endereco.trim(),
        modelo: Number(form.modelo),
        numeroProcessoEmTramite: ehModeloProcessoEmTramite(form.modelo)
          ? form.numeroProcessoEmTramite.trim()
          : null,
      });
      setPedidoCriado(created);
      onSucesso?.(created);
    } catch (err) {
      setError(
        getApiErrorMessage(
          err,
          "Erro ao criar publicação de pedido de licença.",
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBaixarComprovante = async () => {
    if (!pedidoCriado?.id) return;
    setBaixandoComprovante(true);
    setError("");
    try {
      const blob = await publicacaoService.baixarComprovante(pedidoCriado.id);
      downloadBlob(
        blob,
        `comprovante-pedido-licenca-${pedidoCriado.id}.pdf`,
      );
    } catch (err) {
      setError(getApiErrorMessage(err, "Erro ao baixar comprovante."));
    } finally {
      setBaixandoComprovante(false);
    }
  };

  const camposDesabilitados =
    loading || buscandoEmpreendimento || !empreendimentoEncontrado;

  const tipoLicencaSelecionado = tiposLicenca.find(
    (tipo) => tipo.id === form.tipoLicencaId || tipo.Id === form.tipoLicencaId,
  );
  const tipoLicencaNome =
    tipoLicencaSelecionado?.nome || tipoLicencaSelecionado?.Nome || "";
  const mudancaTitularidade = ehModeloNovoTitular(form.modelo);
  const processoEmTramite = ehModeloProcessoEmTramite(form.modelo);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>Nova publicação — Pedido de licença</DialogTitle>
      <DialogContent ref={conteudoRef}>
        {error && (
          <Alert severity="error" sx={{ mt: 1, mb: 2 }}>
            {error}
          </Alert>
        )}
        {pedidoCriado ? (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="success">
              Publicação de pedido de licença criada com sucesso.
            </Alert>
            <Typography variant="body2" color="text.secondary">
              Baixe o comprovante para entregar ao empreendedor. Ele deverá
              anexá-lo na criação da solicitação de licença.
            </Typography>
            <Button
              variant="contained"
              onClick={handleBaixarComprovante}
              disabled={baixandoComprovante}
            >
              {baixandoComprovante ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                "Baixar comprovante"
              )}
            </Button>
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              required
              fullWidth
              autoFocus
              label="CPF/CNPJ"
              name="cnpj"
              value={form.cnpj}
              onChange={handleChange}
              disabled={loading || buscandoEmpreendimento}
              placeholder="000.000.000-00 ou 00.000.000/0000-00"
              inputProps={{ maxLength: 18 }}
              InputProps={{
                endAdornment: buscandoEmpreendimento ? (
                  <InputAdornment position="end">
                    <CircularProgress size={18} />
                  </InputAdornment>
                ) : null,
              }}
              helperText="Informe o CPF ou o CNPJ do empreendimento para carregar os dados"
            />

            {candidatos.length > 1 && (
              <FormControl
                fullWidth
                required
                disabled={loading || buscandoEmpreendimento}
              >
                <InputLabel id="empreendimento-pedido-label">
                  Empreendimento
                </InputLabel>
                <Select
                  labelId="empreendimento-pedido-label"
                  name="empreendimentoId"
                  value={empreendimentoIdSelecionado}
                  label="Empreendimento"
                  onChange={handleSelecionarEmpreendimento}
                >
                  <MenuItem value="">
                    <em>Selecione</em>
                  </MenuItem>
                  {candidatos.map((candidato) => (
                    <MenuItem
                      key={obterIdEmpreendimento(candidato)}
                      value={obterIdEmpreendimento(candidato)}
                    >
                      {rotuloCandidato(candidato, candidatos)}
                    </MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.75 }}>
                  Há mais de um empreendimento com este CPF/CNPJ. Selecione qual
                  receberá a publicação.
                </Typography>
              </FormControl>
            )}

            <TextField
              required
              fullWidth
              label={
                mudancaTitularidade ? "Nome do novo titular" : "Nome / Razão Social"
              }
              name="nomeOuRazaoSocial"
              value={form.nomeOuRazaoSocial}
              onChange={handleChange}
              disabled={camposDesabilitados}
              helperText={
                mudancaTitularidade
                  ? "Nome do novo titular da licença/autorização."
                  : undefined
              }
              inputProps={{ maxLength: CHAR_LIMITS.nomeOuRazaoSocial }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <CharCounter
                      value={form.nomeOuRazaoSocial}
                      maxLength={CHAR_LIMITS.nomeOuRazaoSocial}
                    />
                  </InputAdornment>
                ),
              }}
            />

            <FormControl
              fullWidth
              required
              disabled={camposDesabilitados || loadingTipos}
            >
              <InputLabel id="modelo-publicacao-pedido-label">
                Modelo de publicação
              </InputLabel>
              <Select
                labelId="modelo-publicacao-pedido-label"
                name="modelo"
                value={form.modelo}
                label="Modelo de publicação"
                onChange={handleChange}
              >
                {modelos.map((modelo) => (
                  <MenuItem
                    key={modelo.codigo ?? modelo.Codigo}
                    value={modelo.codigo ?? modelo.Codigo}
                  >
                    {modelo.nome || modelo.Nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              fullWidth
              required
              disabled={camposDesabilitados || loadingTipos}
            >
              <InputLabel id="tipo-licenca-pedido-label">Tipo de licença</InputLabel>
              <Select
                labelId="tipo-licenca-pedido-label"
                name="tipoLicencaId"
                value={form.tipoLicencaId}
                label="Tipo de licença"
                onChange={handleChange}
              >
                <MenuItem value="">
                  <em>Selecione</em>
                </MenuItem>
                {tiposLicenca.map((tipo) => (
                  <MenuItem key={tipo.id} value={tipo.id}>
                    {tipo.nome || tipo.Nome || tipo.codigo || tipo.Codigo}
                  </MenuItem>
                ))}
              </Select>
              {Number(form.modelo) ===
                modeloPublicacaoSistemaEnum.REQUERIMENTO_MUDANCA_TITULARIDADE && (
                <FormHelperText>
                  Incluir o tipo de licença/autorização ambiental.
                </FormHelperText>
              )}
            </FormControl>

            {mudancaTitularidade ? (
              <FormControl fullWidth required disabled={camposDesabilitados}>
                <InputLabel id="categoria-mudanca-titularidade-label">
                  Categoria da licença
                </InputLabel>
                <Select
                  labelId="categoria-mudanca-titularidade-label"
                  name="textoTipoLicenca"
                  value={form.textoTipoLicenca}
                  label="Categoria da licença"
                  onChange={handleChange}
                >
                  <MenuItem value="">
                    <em>Selecione</em>
                  </MenuItem>
                  {categoriasRequerimentoMudancaTitularidade.map((categoria) => (
                    <MenuItem key={categoria} value={categoria}>
                      {categoria}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{HELPER_CATEGORIA_MUDANCA}</FormHelperText>
              </FormControl>
            ) : (
              <TextField
                required
                fullWidth
                multiline
                rows={2}
                label="Tipo de licença/autorização na publicação"
                name="textoTipoLicenca"
                value={form.textoTipoLicenca}
                onChange={handleChange}
                disabled={camposDesabilitados}
                helperText={helperTextoTipoLicenca(form.modelo)}
                inputProps={{ maxLength: CHAR_LIMITS.textoTipoLicenca }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" sx={{ alignSelf: "flex-end", mb: 1 }}>
                      <CharCounter
                        value={form.textoTipoLicenca}
                        maxLength={CHAR_LIMITS.textoTipoLicenca}
                      />
                    </InputAdornment>
                  ),
                }}
              />
            )}

            {processoEmTramite && (
              <TextField
                required
                fullWidth
                label="Número do processo em trâmite na SEMAETS"
                name="numeroProcessoEmTramite"
                value={form.numeroProcessoEmTramite}
                onChange={handleChange}
                disabled={camposDesabilitados}
                helperText="Incluir o número do processo em trâmite na SEMAETS."
                inputProps={{ maxLength: CHAR_LIMITS.numeroProcessoEmTramite }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <CharCounter
                        value={form.numeroProcessoEmTramite}
                        maxLength={CHAR_LIMITS.numeroProcessoEmTramite}
                      />
                    </InputAdornment>
                  ),
                }}
              />
            )}

            <TextField
              required
              fullWidth
              label="Atividade"
              name="atividade"
              value={form.atividade}
              onChange={handleChange}
              disabled={camposDesabilitados}
              helperText="Incluir a atividade objeto do licenciamento (preenchida do empreendimento)"
              inputProps={{ maxLength: CHAR_LIMITS.atividadePublicacao }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <CharCounter
                      value={form.atividade}
                      maxLength={CHAR_LIMITS.atividadePublicacao}
                    />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              required
              fullWidth
              multiline
              rows={2}
              label="Endereço do empreendimento"
              name="endereco"
              value={form.endereco}
              onChange={handleChange}
              disabled={camposDesabilitados}
              helperText="Incluir o endereço completo do local do empreendimento (Rua, Av., etc.)"
              inputProps={{ maxLength: CHAR_LIMITS.enderecoPublicacao }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" sx={{ alignSelf: "flex-end", mb: 1 }}>
                    <CharCounter
                      value={form.endereco}
                      maxLength={CHAR_LIMITS.enderecoPublicacao}
                    />
                  </InputAdornment>
                ),
              }}
            />

            {ehModeloComPrevia(form.modelo) && (
              <PreviaModeloPublicacaoPedido
                modelo={Number(form.modelo)}
                nomeOuRazaoSocial={form.nomeOuRazaoSocial}
                tipoLicencaNome={tipoLicencaNome}
                textoTipoLicenca={form.textoTipoLicenca}
                atividade={form.atividade}
                endereco={form.endereco}
                numeroProcessoEmTramite={form.numeroProcessoEmTramite}
              />
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={loading || baixandoComprovante || buscandoEmpreendimento}
        >
          {pedidoCriado ? "Fechar" : "Cancelar"}
        </Button>
        {!pedidoCriado && (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || buscandoEmpreendimento || !empreendimentoEncontrado}
          >
            {loading ? <CircularProgress size={22} color="inherit" /> : "Criar"}
          </Button>
        )}
      </DialogActions>
      {loadingTipos && !pedidoCriado && (
        <Box sx={{ px: 3, pb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Carregando dados...
          </Typography>
        </Box>
      )}
    </Dialog>
  );
}
